import { Skeleton } from "@/components/ui/skeleton";
import { OrganizationFormResponseField } from "@/components/organization-forms/organization-form-response-field";
import {
  organizationFormContainerClassName,
  organizationFormGridClassName,
} from "@/components/organization-forms/organization-form-layout";
import { PortalPageHeader } from "@/components/portal-page-header";
import { ensureRouteQueryData } from "@/lib/route-loader";
import { numericIdParamSchema } from "@/lib/route-params";
import {
  getOrganizationFormsGetSubmittedResponseQueryOptions,
  useOrganizationFormsGetSubmittedResponse,
} from "@/orval/organization-forms";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

export const Route = createFileRoute("/_app/organizations/$organizationId/admin/forms_/$formId_/responses/$responseId")(
  {
    params: {
      parse: (params) => z.object({ formId: numericIdParamSchema, responseId: numericIdParamSchema }).parse(params),
    },
    loader: ({ params, ...loaderContext }) =>
      ensureRouteQueryData(
        loaderContext,
        getOrganizationFormsGetSubmittedResponseQueryOptions(params.organizationId, params.formId, params.responseId),
      ),
    component: SubmittedResponsePage,
  },
);

function SubmittedResponsePage() {
  const { organizationId, formId, responseId } = Route.useParams();
  const {
    data: response,
    isLoading,
    error,
  } = useOrganizationFormsGetSubmittedResponse(organizationId, formId, responseId);
  if (isLoading) {
    return (
      <div aria-label="Loading submitted response" className="space-y-4 p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (error || !response) {
    return (
      <div role="alert" className="p-6 text-destructive">
        Unable to load this submitted response.
      </div>
    );
  }
  return (
    <div className="flex flex-1 min-h-0 flex-col overflow-y-auto p-4 md:p-6">
      <PortalPageHeader
        className="mb-8"
        title={response.form.name}
        subtitle={`${response.familyName} · Submitted ${response.submittedAt ? new Date(response.submittedAt).toLocaleString() : ""}`}
        breadcrumbs={[
          { label: "Forms", to: "/organizations/$organizationId/admin/forms", params: { organizationId } },
          {
            label: "Submissions",
            to: "/organizations/$organizationId/admin/forms/$formId",
            params: { organizationId, formId },
            search: { tab: "submissions" },
          },
          { label: response.familyName },
        ]}
      />
      <div className={organizationFormContainerClassName}>
        <p className="mb-4 text-sm text-muted-foreground">
          Submitted responses are read-only and show the form as it was when this response was started.
        </p>
        <div className={organizationFormGridClassName}>
          {response.form.fields.map((field) => (
            <OrganizationFormResponseField
              key={field.id}
              field={field}
              value={response.answers[field.id] ?? ""}
              disabled
              onChange={() => undefined}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
